import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './theme.css';
import './App.css';
import './index.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import Register from './pages/Register';
import ChangePassword from './pages/ChangePassword';
import Home from './pages/Home';
import SuperuserDashboard from './pages/SuperuserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import ManageUsers from './pages/ManageUsers';
import ManageTeachers from './pages/ManageTeachers';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ContactPage from './pages/ContactPage';
import SmsConsent from './pages/SmsConsent';
import ErrorPage from './pages/ErrorPage'; // ✅ New import
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import PublicRegistrationPage from './pages/PublicRegistrationPage';
import PrivateRoute from './components/PrivateRoute';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const EnforcePasswordChange = ({ children }) => {
  const { currentUser } = useAuth();
  const passwordChanged = localStorage.getItem('password_changed');
  const role = localStorage.getItem('role');

  if (currentUser && (passwordChanged === 'false' || passwordChanged === false) && role !== 'superuser') {
    return <Navigate to="/change-password" />;
  }

  return children;
};

function App() {
  useEffect(() => {
    const appName = 'MarkazSync';
    const baseTitle = 'Islamic School Management';
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    document.title = isStandalone ? baseTitle : `${appName} | ${baseTitle}`;
  }, []);

  useEffect(() => {
    const splash = document.getElementById('global-splash');
    if (!splash) return;
    splash.style.opacity = '0';
    splash.style.transition = 'opacity 200ms ease';
    const timeoutId = setTimeout(() => splash.remove(), 220);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <Navbar />
          {/* App.js is the frontend route map. PrivateRoute protects screens by role,
              while the backend repeats the same role checks for real security. */}
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<Home />} />
            <Route path="/login" element={<LandingPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register/:markazSlug" element={<PublicRegistrationPage />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/superuser-dashboard" element={
              <PrivateRoute requiredRole="superuser">
                <SuperuserDashboard />
              </PrivateRoute>
            } />
            <Route path="/manage-users" element={
              <PrivateRoute requiredRole="superuser">
                <ManageUsers />
              </PrivateRoute>
            } />
            <Route path="/admin-dashboard" element={
              <PrivateRoute requiredRole="admin">
                <EnforcePasswordChange>
                  <AdminDashboard />
                </EnforcePasswordChange>
              </PrivateRoute>
            } />
            <Route path="/manage-teachers" element={
              <PrivateRoute requiredRole="admin">
                <EnforcePasswordChange>
                  <ManageTeachers />
                </EnforcePasswordChange>
              </PrivateRoute>
            } />
            <Route path="/teacher-dashboard" element={
              <PrivateRoute requiredRole="teacher">
                <EnforcePasswordChange>
                  <TeacherDashboard />
                </EnforcePasswordChange>
              </PrivateRoute>
            } />
            <Route path="/reset-password" element={
              <PrivateRoute requiredRole="superuser">
                <ResetPasswordPage />
              </PrivateRoute>
            } />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sms-consent" element={<SmsConsent />} />
            <Route path="/error" element={<ErrorPage />} /> {/* ✅ Error route */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </Routes>

          <ToastContainer position="top-right" autoClose={3000} />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
